import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar } from "@/components/AppTopBar";

const MARKETPLACES = ["SHOPEE", "TIKTOK", "TOKOPEDIA", "LAZADA", "OTHER"];
const EMPTY = { id: null, store_id: "", store_name_source: "", marketplace: "SHOPEE", account_id: "" };

export default function StoreMappingManagement() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [stores, setStores] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [form, setForm] = useState(null); // null = closed; {..EMPTY} or existing row = open
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = () => {
    api.get("/mappings/stores").then(({ data }) => setRows(data)).catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message));
    api.get("/stores").then(({ data }) => setStores(data.filter((s) => s.status === "ACTIVE"))).catch(() => {});
    api.get("/accounts").then(({ data }) => setWallets(data.filter((a) => a.account_type === "MARKETPLACE_WALLET"))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setError(""); setNotice(""); setForm({ ...EMPTY }); };
  const openEdit = (r) => {
    setError(""); setNotice("");
    setForm({ id: r.id, store_id: r.store_id || "", store_name_source: r.store_name_source || "", marketplace: r.marketplace || "SHOPEE", account_id: r.account_id || "" });
  };
  const closeForm = () => setForm(null);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setNotice("");
    const p = form;
    if (!p.store_id) { setError("Master Toko wajib dipilih."); return; }
    if (!p.store_name_source.trim()) { setError("Source Shop wajib diisi."); return; }
    if (!p.marketplace) { setError("Marketplace wajib dipilih."); return; }
    if (!p.account_id) { setError("Wallet wajib dipilih."); return; }
    try {
      if (p.id) {
        await api.patch(`/mappings/stores/${p.id}`, {
          store_id: p.store_id, store_name_source: p.store_name_source.trim(),
          marketplace: p.marketplace, account_id: p.account_id,
        });
        setNotice("Mapping diperbarui.");
      } else {
        await api.post("/mappings/stores", {
          store_id: p.store_id, store_name_source: p.store_name_source.trim(),
          marketplace: p.marketplace, account_id: p.account_id,
        });
        setNotice(`Mapping "${p.store_name_source.trim()}" ditambahkan.`);
      }
      closeForm(); load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const toggle = async (r) => {
    setError(""); setNotice("");
    try {
      await api.post(`/mappings/stores/${r.id}/${r.is_active ? "deactivate" : "activate"}`);
      load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  if (user && user.role !== "OWNER") {
    return (<div className="app-shell"><AppTopBar active="store-mappings" /><main className="content"><div className="alert-error" data-testid="access-denied">Akses ditolak.</div></main></div>);
  }

  return (
    <div className="app-shell">
      <AppTopBar active="store-mappings" />
      <main className="content">
        {error && <div className="alert-error" data-testid="error-banner">{error}</div>}
        {notice && <div className="alert-ok" data-testid="notice-banner">{notice}</div>}

        <section className="panel" data-testid="mapping-header-panel">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div>
              <h2 className="panel-title" style={{ marginBottom: 4 }}>Manajemen Mapping Toko</h2>
              <p className="muted" style={{ margin: 0 }}>Rantai kanonik: Marketplace + Source Shop → Master Toko → Wallet. Source Shop disimpan apa adanya (mis. SHP-JAYERINDO).</p>
            </div>
            <button className="btn-primary" onClick={openAdd} data-testid="add-mapping-button">+ Tambah Mapping Toko</button>
          </div>
        </section>

        {form && (
          <section className="panel" data-testid="mapping-form-panel">
            <h3 className="panel-title">{form.id ? "Edit Mapping Toko" : "Tambah Mapping Toko"}</h3>
            <form onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12, alignItems: "flex-end" }}>
              <div>
                <label className="field-label">Master Toko (Display Name)</label>
                <select className="field-input" value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value })} data-testid="mapping-store-select">
                  <option value="">— Pilih Toko —</option>
                  {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label">Marketplace</label>
                <select className="field-input" value={form.marketplace} onChange={(e) => setForm({ ...form, marketplace: e.target.value })} data-testid="mapping-marketplace-select">
                  {MARKETPLACES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label">Source Shop (raw)</label>
                <input className="field-input mono" value={form.store_name_source} onChange={(e) => setForm({ ...form, store_name_source: e.target.value })} placeholder="Contoh: SHP-JAYERINDO" data-testid="mapping-source-input" />
              </div>
              <div>
                <label className="field-label">Wallet (Marketplace)</label>
                <select className="field-input" value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })} data-testid="mapping-wallet-select">
                  <option value="">— Pilih Wallet —</option>
                  {wallets.map((w) => <option key={w.account_id} value={w.account_id}>{w.account_name}</option>)}
                </select>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn-primary inline" type="submit" data-testid="mapping-save-button">{form.id ? "Simpan" : "Tambah"}</button>
                <button className="btn-ghost inline" type="button" onClick={closeForm} data-testid="mapping-cancel-button">Batal</button>
              </div>
            </form>
          </section>
        )}

        <section className="panel" data-testid="mapping-list-panel">
          <h3 className="panel-title">Daftar Mapping ({rows.length})</h3>
          <div className="table-scroll">
            <table className="data-table" data-testid="mapping-table">
              <thead><tr>
                <th>Nama Toko / Display Name</th><th>Marketplace</th><th>Source Shop</th>
                <th>Wallet Account</th><th>Status</th><th>Aksi</th>
              </tr></thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={6} className="empty-cell">Belum ada mapping.</td></tr>}
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`mapping-row-${r.id}`}>
                    <td style={{ whiteSpace: "nowrap" }} data-testid={`mapping-store-${r.id}`}>{r.store_name || "-"}</td>
                    <td data-testid={`mapping-marketplace-${r.id}`}>{r.marketplace || "-"}</td>
                    <td className="mono" data-testid={`mapping-source-${r.id}`}>{r.store_name_source}</td>
                    <td data-testid={`mapping-wallet-${r.id}`}>{r.account_name || <span className="muted">(belum diset)</span>}</td>
                    <td><span className={`status-pill ${r.is_active ? "on" : "off"}`} data-testid={`mapping-status-${r.id}`}>{r.is_active ? "AKTIF" : "NONAKTIF"}</span></td>
                    <td>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button className="btn-mini" onClick={() => openEdit(r)} data-testid={`mapping-edit-${r.id}`}>Edit</button>
                        <button className="btn-mini" onClick={() => toggle(r)} data-testid={`mapping-toggle-${r.id}`}>{r.is_active ? "Nonaktifkan" : "Aktifkan"}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
